import { useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'

export interface FlatKeyValueObjectEditorProps {
  /** Objeto plano chave → string (ex. `response_mapping`). */
  value: Record<string, string> | undefined
  onChange: (next: Record<string, string>) => void
  keyLabel?: string
  valueLabel?: string
  disabled?: boolean
  valuePlaceholder?: string
}

/**
 * Mapeamento chave → caminho (texto), sem JSON cru.
 */
export function FlatKeyValueObjectEditor({
  value,
  onChange,
  keyLabel = 'Campo no fluxo',
  valueLabel = 'Caminho na resposta',
  disabled = false,
  valuePlaceholder = 'ex.: api_data.titulos'
}: FlatKeyValueObjectEditorProps) {
  const externalEntries = useMemo(() => Object.entries(value || {}), [value])
  const rowIdRef = useRef(0)
  const [rows, setRows] = useState<Array<{ id: number; key: string; val: string }>>(
    externalEntries.map(([k, v]) => ({ id: ++rowIdRef.current, key: k, val: v }))
  )

  const toObject = (list: Array<{ id: number; key: string; val: string }>): Record<string, string> => {
    const obj: Record<string, string> = {}
    for (const row of list) {
      const key = row.key.trim()
      if (key) obj[key] = row.val
    }
    return obj
  }

  useEffect(() => {
    const projected = toObject(rows)
    const incoming = value || {}
    if (JSON.stringify(projected) === JSON.stringify(incoming)) return
    setRows(externalEntries.map(([k, v]) => ({ id: ++rowIdRef.current, key: k, val: v })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalEntries])

  const commitRows = (next: Array<{ id: number; key: string; val: string }>) => {
    setRows(next)
    onChange(toObject(next))
  }

  const updateRow = (rowId: number, patch: { key?: string; val?: string }) => {
    const copy = rows.map((r) =>
      r.id === rowId
        ? { ...r, key: patch.key !== undefined ? patch.key : r.key, val: patch.val !== undefined ? patch.val : r.val }
        : r
    )
    commitRows(copy)
  }

  const addRow = () => {
    setRows((prev) => [...prev, { id: ++rowIdRef.current, key: '', val: '' }])
  }

  const removeRow = (rowId: number) => commitRows(rows.filter((r) => r.id !== rowId))

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium">{keyLabel}</th>
              <th className="text-left px-2 py-1.5 font-medium">{valueLabel}</th>
              <th className="w-10 px-1 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-2 py-3 text-gray-500 text-center">
                  Nenhum mapeamento. Adicione uma linha.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-t border-gray-100">
                  <td className="p-1">
                    <input
                      type="text"
                      disabled={disabled}
                      value={row.key}
                      onChange={(e) => updateRow(row.id, { key: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                      placeholder="last_api_response_items"
                    />
                  </td>
                  <td className="p-1">
                    <input
                      type="text"
                      disabled={disabled}
                      value={row.val}
                      onChange={(e) => updateRow(row.id, { val: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-200 rounded text-sm font-mono"
                      placeholder={valuePlaceholder}
                    />
                  </td>
                  <td className="p-1 text-center">
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => removeRow(row.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                      aria-label="Remover"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={addRow}
        className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm text-blue-700 border border-blue-200 rounded-md hover:bg-blue-50"
      >
        <Plus className="w-4 h-4" />
        Adicionar mapeamento
      </button>
    </div>
  )
}
